import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import {
  SANDBOX_REDIRECT_EVENT,
  isSandboxRedirectPending,
} from "../hooks/usePaidAccessGuard";
import { redirectToSandbox } from "../utils/redirectToSandbox";
import { logoutWithLog } from "../utils/auth";

const COUNTDOWN_SECONDS = 10;

/**
 * Non-closable popup shown when the paid-access guard determines an unpaid
 * student must leave the portal (Student Portal is paid-only). It does NOT bounce
 * the user to Sandbox silently — it always offers a Logout option, plus a 10s
 * auto-redirect and a "Redirect now" button.
 *
 * - Redirect now / countdown ends → redirectToSandbox() (normal handoff).
 * - Logout → logoutUser() (cy-sso api/logout + clearAuthCookies, which raises the
 *   cross-app `cy_logged_out` signal so Sandbox logs out too), then the login
 *   page. No redirect to Sandbox.
 */
export default function RedirectToSandboxModal() {
  const [open, setOpen] = useState(isSandboxRedirectPending());
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);

  // Show when the guard raises the signal (and immediately if it already fired
  // before this component mounted).
  useEffect(() => {
    const show = () => setOpen(true);
    if (isSandboxRedirectPending()) setOpen(true);
    window.addEventListener(SANDBOX_REDIRECT_EVENT, show);
    return () => window.removeEventListener(SANDBOX_REDIRECT_EVENT, show);
  }, []);

  // Countdown → auto-redirect to Sandbox at zero.
  useEffect(() => {
    if (!open) return undefined;
    if (seconds <= 0) {
      redirectToSandbox();
      return undefined;
    }
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [open, seconds]);

  const handleRedirectNow = () => {
    redirectToSandbox();
  };

  const handleLogout = () => {
    // logoutUser() hits cy-sso api/logout (keepalive) and clearAuthCookies(),
    // which raises the cross-app `cy_logged_out` signal + clears storage — so
    // Sandbox logs out too. No Sandbox redirect; go to the login page.
    logoutWithLog("redirect-to-sandbox-modal");
  };

  return (
    <Dialog
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      BackdropProps={{
        style: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogContent style={{ padding: "28px 24px", textAlign: "center" }}>
        <Typography
          variant="h6"
          style={{ fontWeight: 700, marginBottom: 12 }}
        >
          You are being redirected to Sandbox
        </Typography>
        <Typography
          variant="body1"
          style={{ color: "#555", marginBottom: 20, fontSize: '14px' }}
        >
          Student Portal is only available for paid users.
        </Typography>
        <Typography
          variant="body2"
          style={{ color: "#888", marginBottom: 24 }}
        >
          Redirecting in {seconds} second{seconds === 1 ? "" : "s"}…
        </Typography>
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button variant="outlined" color="secondary" onClick={handleLogout}>
            Logout
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRedirectNow}
          >
            Redirect now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
